import React from "react";
import { motion } from "framer-motion";

// Equirectangular projection: maps (lon,lat) to (x,y) in a 1000x500 viewBox.
const project = (lon, lat) => {
  const x = ((lon + 180) / 360) * 1000;
  const y = ((90 - lat) / 180) * 500;
  return { x, y };
};

// Stylized continental silhouettes (simplified blocks — not geographically perfect, intentionally stylised HUD aesthetic)
const LANDMASS_PATH =
  "M120,140 Q160,100 220,110 Q280,130 320,170 Q300,220 240,240 Q180,240 130,210 Z " + // N. America
  "M260,300 Q300,290 320,330 Q310,400 270,440 Q230,420 240,360 Z " +                   // S. America
  "M460,140 Q510,110 560,130 Q580,170 540,200 Q500,210 470,180 Z " +                   // Europe
  "M520,210 Q580,200 620,250 Q610,330 560,360 Q500,330 510,260 Z " +                   // Africa
  "M620,150 Q720,130 820,170 Q830,230 760,260 Q680,250 620,220 Z " +                   // Asia
  "M820,330 Q870,310 900,340 Q890,380 840,380 Q810,360 820,330 Z";                     // Oceania

export const ThreatMap = ({ points = [] }) => {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-yellow-300/15 bg-black" data-testid="sentinel-threatmap">
      <svg viewBox="0 0 1000 500" className="w-full h-full block" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="hud-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0a0a0a" />
            <stop offset="100%" stopColor="#0d0d12" />
          </linearGradient>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(255,193,7,0.06)" strokeWidth="0.5" />
          </pattern>
          <radialGradient id="pulse-glow">
            <stop offset="0%" stopColor="white" stopOpacity="0.9" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="1000" height="500" fill="url(#hud-bg)" />
        <rect x="0" y="0" width="1000" height="500" fill="url(#grid)" />
        {/* Scanline */}
        <motion.rect
          x="0" width="1000" height="2" fill="rgba(34,211,238,0.18)"
          initial={{ y: 0 }}
          animate={{ y: 500 }}
          transition={{ duration: 6, ease: "linear", repeat: Infinity }}
        />
        {/* Landmasses */}
        <path d={LANDMASS_PATH} fill="rgba(255,193,7,0.08)" stroke="rgba(255,193,7,0.35)" strokeWidth="0.8" />
        {/* Equator + meridian */}
        <line x1="0" y1="250" x2="1000" y2="250" stroke="rgba(255,193,7,0.12)" strokeWidth="0.5" strokeDasharray="4 6" />
        <line x1="500" y1="0" x2="500" y2="500" stroke="rgba(255,193,7,0.12)" strokeWidth="0.5" strokeDasharray="4 6" />
        {/* Threat pulses */}
        {points.map((p, i) => {
          const { x, y } = project(p.lon, p.lat);
          const r = Math.max(4, Math.min(14, 4 + (p.count || 1) * 1.5));
          return (
            <g key={`${p.tag}-${i}`} data-testid={`threat-pulse-${p.tag}`}>
              <motion.circle
                cx={x} cy={y} r={r} fill="url(#pulse-glow)"
                initial={{ opacity: 0.6, scale: 1 }}
                animate={{ opacity: [0.6, 0, 0.6], scale: [1, 2.6, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, delay: (i % 6) * 0.3 }}
                style={{ mixBlendMode: "screen", color: p.color }}
              />
              <circle cx={x} cy={y} r={r * 0.55} fill={p.color} stroke="white" strokeWidth="0.6" />
              <text x={x + r + 4} y={y + 3} fontSize="9" fill="rgba(255,255,255,0.65)" fontFamily="monospace">
                {p.label} · {p.count}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="absolute top-2 left-3 text-[10px] tracking-[0.3em] uppercase text-yellow-300/70 font-display">
        Live Threat Telemetry · Last 24h Aggregate
      </div>
      <div className="absolute bottom-2 right-3 text-[10px] tracking-[0.2em] uppercase text-cyan-300/60 font-mono flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse" /> SCANNING
      </div>
    </div>
  );
};

export default ThreatMap;
